///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { LinkFeature } from '../../../entities/link/link-feature';
import { GtthRemoveLinkCommand } from './remove-link-command';
import { ActionsToolbarModeEnum, GtthStepGroupEnum } from '../../../../../generated/shared-types.model';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthModifyUnitInfoCommand } from '../node/modify-unit-info-command';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthNodeFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { TopologyLinkTypeEnum } from '../../../../../services/project-socket/dto/topology-types.model';
import { isDefined } from '@siklu/sui-lib/utils';

export class GtthCreateLinkCommand extends GtthAbstractCommand {
    public linkId: number;
    private lineJson: GtthLinkFeatureDtoModel;
    private nodes: GtthNodeFeatureDtoModel[];
    private toolbarMode: ActionsToolbarModeEnum;

    private fromId: number;
    private toId: number;

    private nodeFromJson: GtthNodeFeatureDtoModel;
    private nodeToJson: GtthNodeFeatureDtoModel;

    getCommandName(): string {
        return 'GtthCreateLinkCommand';
    }

    createFromIds(fromStationId: number, toStationId: number, currentMode: ActionsToolbarModeEnum): this {
        this.fromId = fromStationId;
        this.toId = toStationId;
        this.toolbarMode = currentMode;
        return this;
    }

    createFromGeoJson(geoJson: GtthLinkFeatureDtoModel): this {
        this.linkId = geoJson.getId();
        this.lineJson = geoJson;
        return this;
    }

    doCommand(): void {
        let link: LinkFeature;

        if (this.fromId && this.toId) {
            this.getLinkStations(this.fromId, this.toId);

            if (!this.nodeFromJson.getIsEnabled() || !this.nodeToJson.getIsEnabled()) {
                this.commandResolve(false);
                return;
            }
        }

        if (this.lineJson) {
            link = LinkFeature.parseGeoJson(this.lineJson, this.historyService);
            this.getLinkStations(link.getFromId(), link.getToId());
        } else {
            if (this.fromId === this.toId) {
                this.historyService.notificationService.showError('You can not create a link to same station!');
                this.commandResolve(false);
                return;
            }

            if (this.historyService.logicalTopology.containsLinkByStationsId(this.fromId, this.toId)) {
                this.historyService.notificationService.showError('Link already exist!');
                this.commandResolve(false);
                return;
            }

            link = this.createNewLink();
            this.lineJson = link.toGeoJson();
        }

        this.linkId = link.getId();

        if (!this.isDoLocally && !link.getLinkIsDisabled()) {
            this.nodes = [this.nodeFromJson, this.nodeToJson];

            this.nodes.forEach((node) => {
                if (!node.getIsEnabled()) {
                    const unitModifyCommand = this.historyService.command(GtthModifyUnitInfoCommand).create(node.getId());
                    unitModifyCommand.setNewIsEnableValue(true);
                    this.historyService.doCommandBypassHistory(unitModifyCommand);
                }
            });
        }

        const activity = this.createActivityDTO(false);

        activity.links = [this.lineJson];

        this.sendActivity(activity);
    }

    undoCommand(): void {
        if (this.nodeFromJson || this.nodeToJson) {
            [this.nodeFromJson, this.nodeToJson].forEach((node) => {
                if (!node.getIsEnabled()) {
                    const unitModifyCommand = this.historyService.command(GtthModifyUnitInfoCommand).create(node.getId());
                    unitModifyCommand.setNewIsEnableValue(false);
                    this.historyService.doCommandBypassHistory(unitModifyCommand);
                }
            });
        }

        const removeCommand = this.historyService.command(GtthRemoveLinkCommand).createFromId(this.linkId);
        this.historyService.doCommandBypassHistory(removeCommand).then((res: boolean) => {
            this.commandResolve(res);
        });
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            if (this.lineJson) {
                const removeCommand = this.historyService.command(GtthRemoveLinkCommand).createFromId(this.linkId);
                this.historyService.doCommandBypassHistory(removeCommand, true);
            }
        } else {
            const link = this.historyService.logicalTopology.getLink(activityDto.links[0].getId());

            if (link) {
                link.parseGeoJson(activityDto.links[0]);
                this.linkId = link.getId();
            } else {
                const newLink = LinkFeature.parseGeoJson(activityDto.links[0], this.historyService);
                this.getLinkStations(newLink.getFromId(), newLink.getToId());

                this.registerLink(
                    newLink,
                    this.nodeFromJson.getId(),
                    this.nodeFromJson.getName(),
                    this.nodeToJson.getId(),
                    this.nodeToJson.getName()
                );
                this.drawLink(newLink);
                this.linkId = newLink.getId();

                this.historyService.interactiveWindowsService.linkCreate(
                    this.nodeFromJson.getName(),
                    this.nodeToJson.getName(),
                    newLink.getLinkIsDisabled()
                );
            }
        }

        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return true;
    }

    private createNewLink(): LinkFeature {
        const link = new LinkFeature(this.fromId, this.toId, this.historyService);
        this.getLinkStations(this.fromId, this.toId);

        switch (this.toolbarMode) {
            case ActionsToolbarModeEnum.NOT_CONFIRMED_LOS:
                link.setLinkType(TopologyLinkTypeEnum.NOT_CONFIRMED_LOS);
                break;
            case ActionsToolbarModeEnum.CONFIRMED_LOS:
                link.setLinkType(TopologyLinkTypeEnum.CONFIRMED_LOS);
                break;
            case ActionsToolbarModeEnum.NO_LOS:
                link.setLinkType(TopologyLinkTypeEnum.NO_LOS);
                break;
            case ActionsToolbarModeEnum.LINK:
                link.setLinkType(TopologyLinkTypeEnum.LINK);
                break;
            case ActionsToolbarModeEnum.FIBER:
                link.setLinkType(TopologyLinkTypeEnum.FIBER);
                break;
            default:
                link.setLinkType(TopologyLinkTypeEnum.NOT_CONFIRMED_LOS);
                break;
        }

        link.setLinkIsDisabled(false);

        return link;
    }

    private registerLink(link: LinkFeature, nodeFromId: number, nodeFromName: string, nodeToId: number, nodeToName: string) {
        this.historyService.logicalTopology.addLink(link);

        this.historyService.logicalTopology.addLineToStationById(nodeFromId, link.getId());
        this.historyService.logicalTopology.addLineToStationById(nodeToId, link.getId());
    }

    private drawLink(link: LinkFeature) {
        if (this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES) {
            if (link.draw()) {
                link.initListeners();
            }
        } else if (this.historyService.logicalTopology.isShowLinkInCurrentState(link)) {
            if (link.draw()) {
                link.initListeners();
            } else {
                link.updateLinkStatus();
            }
        }
    }

    private getLinkStations(fromId, toId) {
        if (isDefined(fromId) && !this.nodeFromJson) {
            this.nodeFromJson = this.historyService.logicalTopology.getStation(fromId).toGeoJson();
        }
        if (isDefined(toId) && !this.nodeToJson) {
            this.nodeToJson = this.historyService.logicalTopology.getStation(toId).toGeoJson();
        }
    }
}
